import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '@/shared/services/enhancedApi';
import { usePlanSelection } from '../contexts/PlanSelectionContext';
import { useAppStore } from '@/store/useAppStore';

interface PabaCalculateParams {
  age: number;
  price: number;
}

interface PabaInfo {
  id: number;
  inclusive_age: string;
  operations: string;
}

interface PabaResponse {
  success: boolean;
  code: number;
  data: {
    org_price: number;
    adj_price: number;
    paba_info: PabaInfo;
  };
  message: string;
}

interface UseUpdatedPremiumPricebyAgeReturn {
  originalPrice: number | null;
  adjustedPrice: number | null;
  pabaInfo: PabaInfo | null;
  loading: boolean;
  error: string | null;
  calculatePremiumByAge: (params: PabaCalculateParams) => Promise<void>;
  clearCalculation: () => void;
}

export const useUpdatedPremiumPricebyAge = (): UseUpdatedPremiumPricebyAgeReturn => {
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);
  const [adjustedPrice, setAdjustedPrice] = useState<number | null>(null);
  const [pabaInfo, setPabaInfo] = useState<PabaInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setAdjustmentPriceByAge } = useAppStore();

  // Access plan selection context to get forex conversion data
  const { getTempDataValue } = usePlanSelection();

  const calculatePremiumByAge = useCallback(async (params: PabaCalculateParams) => {
    setLoading(true);
    setError(null);

    try {
      // Validate input parameters
      if (!params.age || params.age <= 0) {
        throw new Error('Age must be a positive number');
      }
      
      if (!params.price || params.price <= 0) {
        throw new Error('Price must be a positive number');
      }

      // ENHANCED: Check if we should use converted currency amount for calculation
      const isForexConverted = getTempDataValue('isForexConverted') as boolean;
      const convertedRateAmount = getTempDataValue('convertedRateAmount') as number;
      const convertedCurrencyCode = getTempDataValue('convertedCurrencyCode') as string;
      
      // Determine the price to use for calculation
      let calculationPrice = params.price;
      let currencyInfo = '';
      
      if (isForexConverted && convertedRateAmount && convertedRateAmount > 0) {
        // Use converted amount for PABA calculation
        calculationPrice = convertedRateAmount;
        currencyInfo = ` (using converted ${convertedCurrencyCode} amount: ${convertedRateAmount})`;
        console.log('useUpdatedPremiumPricebyAge: Using converted currency for calculation:', {
          originalPrice: params.price,
          convertedPrice: calculationPrice,
          convertedCurrency: convertedCurrencyCode
        });
      } else {
        currencyInfo = ` (using original amount: ${params.price})`;
        console.log('useUpdatedPremiumPricebyAge: Using original price for calculation:', params.price);
      }

      const requestBody = {
        age: params.age,
        price: calculationPrice
      };

      console.log(`Calculating premium price by age with params${currencyInfo}:`, requestBody);

      const data: PabaResponse = await enhancedApi.post('/api/paba/calculate', requestBody, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      }, 1); // High priority for premium calculations

      if (data.success && data.data) {
        setOriginalPrice(data.data.org_price);
        setAdjustedPrice(data.data.adj_price);
        setPabaInfo(data.data.paba_info);
        
        setAdjustmentPriceByAge(data.data.adj_price);

        console.log('Successfully calculated premium price adjustment:', {
          originalPrice: data.data.org_price,
          adjustedPrice: data.data.adj_price,
          pabaInfo: data.data.paba_info
        });
        
        if (data.message) {
          console.log('Calculation message:', data.message);
        }
      } else {
        throw new Error(data.message || 'Failed to calculate premium price adjustment');
      }
    } catch (err) {
      console.error('Error calculating premium price by age:', err);
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [getTempDataValue]);

  const handleError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
      if (err.response) {
        // Server responded with error status
        const errorMessage = err.response.data?.message || err.message;
        setError(`Server error: ${err.response.status} - ${errorMessage}`);
      } else if (err.request) {
        // Request was made but no response received
        setError('Network error: Unable to connect to server');
      } else {
        // Something else happened
        setError(`Request error: ${err.message}`);
      }
    } else {
      setError(err instanceof Error ? err.message : 'Failed to calculate premium price');
    }
    
    // Clear previous data on error
    setOriginalPrice(null);
    setAdjustedPrice(null);
    setPabaInfo(null);
  };

  const clearCalculation = useCallback(() => {
    setOriginalPrice(null);
    setAdjustedPrice(null);
    setPabaInfo(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    originalPrice,
    adjustedPrice,
    pabaInfo,
    loading,
    error,
    calculatePremiumByAge,
    clearCalculation
  };
};
